<template>
  <div class="page-content practice-assessment">
    <div class="page-title">
      <h5>实践考核</h5>
    </div>

    <div class="practice-body" v-loading="loading">
      <template v-if="batches.length">
        <div
          v-for="batch in batches"
          :key="batch.id"
          class="batch-card"
        >
          <div class="batch-header">
            <div class="batch-title-row">
              <span class="batch-name">{{ batch.name }}</span>
            </div>
            <div class="batch-time">
              报告上传时间：{{ batch.reportStartTime }} 至 {{ batch.reportEndTime }}
            </div>
          </div>

          <div class="course-list">
            <div
              v-for="course in batch.courses"
              :key="course.id"
              class="course-item"
            >
              <div class="course-main">
                <div class="course-info">
                  <div class="course-name-row">
                    <span class="course-name">
                      {{ course.courseCode }}-{{ course.courseName }}
                    </span>
                    <span
                      v-if="course.publishStatus !== '1'"
                      class="status-tag status-tag--sm"
                      :class="getStatusClass(course)"
                    >
                      {{ getStatusText(course) }}
                    </span>
                  </div>

                  <div class="teacher-info" v-if="course.teacherName || course.teacherPhone">
                    <span class="teacher-label">考核老师：</span>
                    <span class="teacher-name">{{ course.teacherName || '--' }}</span>
                    <span class="teacher-phone" v-if="course.teacherName && course.teacherPhone">&nbsp;{{ course.teacherPhone }}</span>
                  </div>
                  <div class="teacher-info" v-else>
                    <span class="teacher-label">考核老师：--</span>
                  </div>

                  <div class="file-row">
                    <div v-if="course.reportFileName" class="file-link">
                      <a :href="course.reportAtt" target="_blank" rel="noopener">
                        {{ course.reportFileName }}
                      </a>
                    </div>
                    <el-button
                      class="requirement-btn"
                      type="primary"
                      size="mini"
                      @click="openRequirement(course)"
                    >
                      考核要求
                    </el-button>
                  </div>

                  <div
                    v-if="course.videoAtt"
                    class="practice-video-info"
                    @click="previewVideo(course.videoAtt)"
                  >
                    <span class="practice-video-name">
                      <i class="el-icon-video-camera"></i>
                      {{ course.videoName || '实践视频.mp4' }}
                    </span>
                    <span
                      v-if="course.videoSize"
                      class="practice-video-size"
                    >
                      {{ formatFileSize(course.videoSize) }}
                    </span>
                  </div>

                  <div
                    v-if="course.reviewStatus === 'returned' && course.reviewComments"
                    class="review-box"
                  >
                    <div class="review-label">评阅意见</div>
                    <div class="review-content">{{ course.reviewComments }}</div>
                  </div>
                  <div
                    v-if="course.auditStatus === 'rejected' && course.auditComments"
                    class="review-box"
                  >
                    <div class="review-label">审核意见</div>
                    <div class="review-content">{{ course.auditComments }}</div>
                  </div>
                </div>

                <div class="course-action">
                  <div v-if="course.score != null" class="score" :class="scoreClass(course.score)">
                    {{ course.score }}分
                  </div>
                  <div
                    v-else-if="course.publishStatus === '1'"
                    class="score-pending"
                  >
                    待发布
                  </div>
                  <template v-else-if="canOperate(batch, course)">
                    <el-button
                      v-if="!course.reportAtt"
                      type="primary"
                      size="small"
                      @click="openUpload(course, 'upload')"
                    >
                      上传报告
                    </el-button>
                    <el-button
                      v-else
                      size="small"
                      plain
                      @click="openUpload(course, 'modify')"
                    >
                      修改报告
                    </el-button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <Empty v-else-if="!loading" />
    </div>

    <el-dialog
      :title="uploadDialogTitle"
      :visible.sync="uploadDialogVisible"
      width="720px"
      custom-class="upload-report-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="!submitting"
      :show-close="!submitting"
      append-to-body
      :before-close="handleDialogBeforeClose"
      @closed="resetUploadForm"
    >
      <div class="upload-dialog-body">
        <div class="upload-course-card">
          <div class="upload-course-card__icon">
            <i class="el-icon-notebook-2"></i>
          </div>
          <div class="upload-course-card__info">
            <div class="upload-course-card__label">当前课程</div>
            <div class="upload-course-card__name">
              {{ currentCourseDisplayName }}
            </div>
            <div v-if="currentBatch" class="upload-course-card__batch">
              {{ currentBatch.name }}
            </div>
          </div>
          <span class="upload-mode-tag" :class="uploadMode === 'modify' ? 'is-modify' : 'is-new'">
            {{ uploadMode === 'modify' ? '修改报告' : '首次上传' }}
          </span>
        </div>

        <div
          v-if="uploadMode === 'modify' && currentCourse && currentCourse.reportFileName"
          class="upload-current-file"
        >
          <div class="upload-current-file__label">当前报告</div>
          <div class="upload-current-file__content">
            <i class="el-icon-document"></i>
            <a :href="currentCourse.reportAtt" target="_blank" rel="noopener">
              {{ currentCourse.reportFileName }}
            </a>
          </div>
        </div>

        <div
          v-if="uploadMode === 'modify' && currentVideoName && !selectedVideoFile"
          class="upload-current-file"
        >
          <div class="upload-current-file__label">当前视频</div>
          <div class="upload-current-file__content">
            <i class="el-icon-video-camera"></i>
            <span class="video-current-name">{{ currentVideoName }}</span>
            <span class="video-current-size">（{{ formatFileSize(currentVideoSize) }}）</span>
            <el-button type="text" size="mini" @click="previewVideo(currentVideoAtt)">
              查看视频
            </el-button>
            <el-button type="text" size="mini" class="video-delete-btn" @click="deleteCurrentVideo">
              删除视频
            </el-button>
          </div>
        </div>

        <div
          v-if="currentCourse && currentCourse.reviewStatus === 'returned' && currentCourse.reviewComments"
          class="upload-review-tip"
        >
          <div class="upload-review-tip__title">
            <i class="el-icon-warning-outline"></i>
            评阅意见
          </div>
          <div class="upload-review-tip__content">{{ currentCourse.reviewComments }}</div>
        </div>
        <div
          v-if="currentCourse && currentCourse.auditStatus === 'rejected' && currentCourse.auditComments"
          class="upload-review-tip"
        >
          <div class="upload-review-tip__title">
            <i class="el-icon-warning-outline"></i>
            审核意见
          </div>
          <div class="upload-review-tip__content">{{ currentCourse.auditComments }}</div>
        </div>

        <div class="upload-section">
          <div class="upload-section__title">
            <span>{{ uploadMode === 'modify' ? '选择新报告文件（可选）' : '选择报告文件' }}</span>
          </div>
          <div class="upload-section__row">
            <el-upload
              ref="uploadRef"
              class="upload-area"
              drag
              action=""
              :auto-upload="false"
              :limit="1"
              :file-list="fileList"
              :on-change="handleFileChange"
              :on-remove="handleFileRemove"
              :on-exceed="handleExceed"
              accept=".doc,.docx,.pdf,.ppt,.pptx"
            >
              <div class="upload-dragger-content">
                <div class="upload-dragger-content__icon">
                  <i class="el-icon-upload2"></i>
                </div>
                <div class="el-upload__text">
                  将文件拖到此处，或<em>点击选择文件</em>
                </div>
                <div class="upload-type-tags">
                  <span class="type-tag type-tag--word">Word</span>
                  <span class="type-tag type-tag--ppt">PPT</span>
                  <span class="type-tag type-tag--pdf">PDF</span>
                </div>
              </div>
            </el-upload>
            <div class="upload-section__tips">
              <p>支持 .doc、.docx、.ppt、.pptx、.pdf 格式，单个文件上传。</p>
              <template v-if="uploadMode === 'modify'">
                <p>可选择新文件替换当前报告，或不选文件直接重新提交当前报告。</p>
                <p>点击「确认修改」后将提交并进入评阅流程。</p>
              </template>
              <template v-else>
                <p>文件将在您点击「确认提交」后上传并提交，取消不会产生任何记录。</p>
                <p>提交后将进入评阅流程，请确认文件内容无误。</p>
              </template>
            </div>
          </div>
        </div>

        <div class="upload-section">
          <div class="upload-section__title">
            <span>视频附件（可选）</span>
          </div>
          <div class="upload-section__row">
            <el-upload
              ref="videoUploadRef"
              class="upload-area"
              drag
              action=""
              :auto-upload="false"
              :limit="1"
              :file-list="videoFileList"
              :on-change="handleVideoChange"
              :on-remove="handleVideoRemove"
              :on-exceed="handleVideoExceed"
              accept=".mp4,video/mp4"
            >
              <div class="upload-dragger-content">
                <div class="upload-dragger-content__icon">
                  <i class="el-icon-video-camera"></i>
                </div>
                <div class="el-upload__text">
                  将视频拖到此处，或<em>点击选择视频</em>
                </div>
                <div class="upload-type-tags">
                  <span class="type-tag type-tag--video">MP4</span>
                </div>
              </div>
            </el-upload>
            <div class="upload-section__tips">
              <p>仅支持MP4格式，文件大小不超过500M，最多上传1个视频。</p>
              <template v-if="uploadMode === 'modify'">
                <p>可选择新视频替换当前视频，或删除当前视频，视频为选填项。</p>
              </template>
              <template v-else>
                <p>视频为选填项，不选择也可正常提交报告。</p>
              </template>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="mini" :disabled="submitting" @click="uploadDialogVisible = false">
          取 消
        </el-button>
        <el-button
          size="mini"
          type="primary"
          :loading="submitting"
          :disabled="!canConfirmUpload"
          @click="confirmUpload"
        >
          {{ confirmButtonText }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="视频预览"
      :visible.sync="videoPreviewVisible"
      width="720px"
      custom-class="video-preview-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="true"
      append-to-body
      @closed="handleVideoPreviewClose"
    >
      <video
        ref="videoPlayer"
        :src="videoPreviewUrl"
        controls
        playsinline
        preload="metadata"
        style="display:block;width:100%;max-height:520px;background:#000;"
      >
        当前浏览器不支持视频播放
      </video>
    </el-dialog>

    <el-dialog
      :title="requirementTitle"
      :visible.sync="requirementVisible"
      width="560px"
      custom-class="requirement-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="true"
      append-to-body
      @closed="handleRequirementClose"
    >
      <div class="requirement-body" v-loading="requirementLoading">
        <div v-if="currentRequirement && currentRequirement.requirementContent" class="requirement-content">
          <div v-html="currentRequirement.requirementContent"></div>
        </div>
        <div v-else class="requirement-empty">
          暂未设置考核要求
        </div>

          <div class="requirement-attachments" v-if="requirementAttachments.length > 0">
            <div class="attachment-title">附件文档</div>
            <div
              v-for="file in requirementAttachments"
              :key="file.id"
              class="attachment-item"
            >
              <i class="el-icon-document"></i>
              <a
                :href="resolveFileUrl(file.fileUrl)"
                target="_blank"
                rel="noopener"
                class="attachment-link"
              >
                {{ file.fileName }}
              </a>
            </div>
          </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="requirementVisible = false">
          关 闭
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Empty from '@/components/Empty.vue'
import { getPracticeAssessmentList, uploadPracticeReport, getStudentAssessmentRequirement } from '@/api/practiceAssessment'

const PASS_SCORE = 60

const MAX_VIDEO_SIZE = 524288000

export default {
  name: 'PracticeAssessment',
  components: { Empty },
  data() {
    return {
      loading: false,
      submitting: false,
      batches: [],
      uploadDialogVisible: false,
      uploadDialogTitle: '上传报告',
      currentCourse: null,
      currentBatch: null,
      uploadMode: 'upload',
      fileList: [],
      selectedFile: null,
      videoFileList: [],
      selectedVideoFile: null,
      currentVideoAtt: '',
      currentVideoName: '',
      currentVideoSize: '',
      removeVideo: false,
      videoPreviewVisible: false,
      videoPreviewUrl: '',
      requirementVisible: false,
      requirementLoading: false,
      currentRequirement: {
        requirementContent: '',
        attachments: []
      },
      requirementTitle: ''
    }
  },
  computed: {
    currentCourseDisplayName() {
      if (!this.currentCourse) return '-'
      const { courseCode, courseName } = this.currentCourse
      const name = (courseName || '').trim()
      if (courseCode && name) return `${courseCode}-${name}`
      if (courseCode) return courseCode
      if (name) return name
      return '未知课程'
    },

    confirmButtonText() {
      return this.uploadMode === 'modify' ? '确认修改' : '确认提交'
    },

    canConfirmUpload() {
      if (this.uploadMode === 'upload') {
        return !!this.selectedFile
      }
      if (this.selectedFile) return true
      const course = this.currentCourse
      return !!(course && course.reportAtt && course.reportFileName)
    },

    requirementAttachments() {
      const attachments =
        this.currentRequirement &&
        this.currentRequirement.attachments

      return Array.isArray(attachments)
        ? attachments
        : []
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        this.batches = await getPracticeAssessmentList()
      } finally {
        this.loading = false
      }
    },

    getStatusText(course) {
      const { publishStatus, reviewStatus } = course

      // 已发布 - 显示成绩
      if (publishStatus === '1') {
        return ''
      }

      // 评阅状态
      if (reviewStatus) {
        const reviewMap = {
          pending: '待评阅',
          reviewed: '已评阅',
          returned: '打回修改'
        }
        return reviewMap[reviewStatus] || ''
      }

      // 刚报名，状态为空 - 显示待提交
      return '待提交'
    },

    getStatusClass(course) {
      const { publishStatus, reviewStatus } = course

      // 已发布 - 无标签
      if (publishStatus === '1') {
        return ''
      }

      // 评阅状态样式
      if (reviewStatus) {
        const reviewClassMap = {
          pending: 'status-tag--warning',
          reviewed: 'status-tag--success',
          returned: 'status-tag--danger'
        }
        return reviewClassMap[reviewStatus] || ''
      }

      // 刚报名，状态为空 - 显示待提交样式（灰色）
      return 'status-tag--info'
    },

    scoreClass(score) {
      return score >= PASS_SCORE ? 'score--pass' : 'score--fail'
    },

    isInUploadPeriod(batch) {
      const now = Date.now()
      const start = new Date(batch.reportStartTime + ' 00:00:00').getTime()
      const end = new Date(batch.reportEndTime + ' 23:59:59').getTime()
      return now >= start && now <= end
    },

    canOperate(batch, course) {
      if (batch.status === 1) return false
      if (!this.isInUploadPeriod(batch)) return false
      if (course.publishStatus === '1') return false
      if (course.auditStatus) return false
      // 允许刚报名（状态为空）或评阅状态为pending/returned的课程上传报告
      return !course.reviewStatus || course.reviewStatus === 'pending' || course.reviewStatus === 'returned'
    },

    openUpload(course, mode) {
      this.currentCourse = course
      this.currentBatch = this.batches.find(batch =>
        batch.courses.some(item => item.id === course.id)
      ) || null
      this.uploadMode = mode
      this.uploadDialogTitle = mode === 'modify' ? '修改报告' : '上传报告'
      this.currentVideoAtt = course.videoAtt || ''
      this.currentVideoName = course.videoName || ''
      this.currentVideoSize = course.videoSize || ''
      this.selectedVideoFile = null
      this.videoFileList = []
      this.removeVideo = false
      this.uploadDialogVisible = true
    },

    handleFileChange(file, fileList) {
      const suffix = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      const allowed = ['.doc', '.docx', '.pdf', '.ppt', '.pptx']
      if (!allowed.includes(suffix)) {
        this.$message.error('文件格式不正确，支持 word、ppt、pdf 文件')
        this.fileList = []
        this.selectedFile = null
        return
      }
      this.fileList = fileList.slice(-1)
      this.selectedFile = file.raw
    },

    handleFileRemove() {
      this.fileList = []
      this.selectedFile = null
    },

    handleExceed() {
      this.$message.warning('只能上传一个文件，请先移除已选文件')
    },

    handleVideoChange(file, fileList) {
      const rawFile = file.raw || file
      if (!rawFile) return

      if (!/\.mp4$/i.test(rawFile.name)) {
        this.$message.error('视频仅支持MP4格式')
        this.videoFileList = []
        this.selectedVideoFile = null
        return
      }

      if (rawFile.size <= 0) {
        this.$message.error('视频文件不能为空')
        this.videoFileList = []
        this.selectedVideoFile = null
        return
      }

      if (rawFile.size > MAX_VIDEO_SIZE) {
        this.$message.error('视频大小不能超过500MB')
        this.videoFileList = []
        this.selectedVideoFile = null
        return
      }

      this.videoFileList = fileList.slice(-1)
      this.selectedVideoFile = rawFile
      this.removeVideo = false
    },

    handleVideoRemove() {
      this.videoFileList = []
      this.selectedVideoFile = null
    },

    handleVideoExceed() {
      this.$message.warning('最多只能上传一个视频文件')
    },

    deleteCurrentVideo() {
      this.currentVideoAtt = ''
      this.currentVideoName = ''
      this.currentVideoSize = ''
      this.selectedVideoFile = null
      this.videoFileList = []
      this.removeVideo = true
      if (this.$refs.videoUploadRef) {
        this.$refs.videoUploadRef.clearFiles()
      }
    },

    previewVideo(url) {
      if (!url) {
        this.$message.error('视频地址不存在')
        return
      }
      this.videoPreviewUrl = url
      this.videoPreviewVisible = true
    },

    handleVideoPreviewClose() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
        this.$refs.videoPlayer.currentTime = 0
      }
      this.videoPreviewUrl = ''
    },

    formatFileSize(size) {
      const bytes = Number(size)
      if (!Number.isFinite(bytes) || bytes <= 0) return '-'
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(2)} KB`
      }
      return `${(bytes / 1024 / 1024).toFixed(2)} MB`
    },

    handleDialogBeforeClose(done) {
      if (this.submitting) return
      done()
    },

    resetUploadForm() {
      this.currentCourse = null
      this.currentBatch = null
      this.fileList = []
      this.selectedFile = null
      this.videoFileList = []
      this.selectedVideoFile = null
      this.currentVideoAtt = ''
      this.currentVideoName = ''
      this.currentVideoSize = ''
      this.removeVideo = false
      this.videoPreviewVisible = false
      this.videoPreviewUrl = ''
      if (this.$refs.uploadRef) {
        this.$refs.uploadRef.clearFiles()
      }
      if (this.$refs.videoUploadRef) {
        this.$refs.videoUploadRef.clearFiles()
      }
    },

    resolveUploadFileUrl(uploadRes) {
      if (!uploadRes) return ''
      if (typeof uploadRes === 'string') return uploadRes
      if (typeof uploadRes === 'object') {
        return uploadRes.res || uploadRes.fullpath || uploadRes.url || uploadRes.data || ''
      }
      return ''
    },

    async confirmUpload() {
      if (!this.canConfirmUpload || !this.currentCourse) return

      this.submitting = true
      try {
        let reportAtt
        let reportFileName
        const isResubmitOnly = this.uploadMode === 'modify' && !this.selectedFile

        if (isResubmitOnly) {
          reportAtt = this.currentCourse.reportAtt
          reportFileName = this.currentCourse.reportFileName
        } else {
          reportFileName = this.selectedFile.name
          const formData = new FormData()
          formData.append('file', this.selectedFile)

          const uploadRes = await this.$http.post('/student/pc/common/uploadFile2', formData, {
            'Content-Type': 'multipart/form-data'
          })
          reportAtt = this.resolveUploadFileUrl(uploadRes)

          if (!reportAtt) {
            this.$message.error('文件上传失败，未获取到文件地址')
            return
          }
        }

        const params = {
          id: this.currentCourse.id,
          reportAtt,
          reportFileName
        }

        if (this.selectedVideoFile) {
          const videoFormData = new FormData()
          videoFormData.append('file', this.selectedVideoFile)

          const videoUploadRes = await this.$http.post('/student/pc/common/uploadFile2', videoFormData, {
            'Content-Type': 'multipart/form-data'
          })
          const videoAtt = this.resolveUploadFileUrl(videoUploadRes)

          if (!videoAtt) {
            this.$message.error('视频上传失败，未获取到视频地址')
            return
          }

          params.videoAtt = videoAtt
          params.videoName = this.selectedVideoFile.name
          params.videoSize = String(this.selectedVideoFile.size)
        } else if (this.removeVideo) {
          params.removeVideo = true
        }

        await uploadPracticeReport(params)

        const successMsg = isResubmitOnly
          ? '报告重新提交成功'
          : this.uploadMode === 'modify'
            ? '报告修改成功'
            : '报告提交成功'
        this.$message.success(successMsg)
        this.uploadDialogVisible = false
        await this.fetchList()
      } catch (e) {
        // request 层已处理错误提示
      } finally {
        this.submitting = false
      }
    },

    resolveFileUrl(fileUrl) {
      if (!fileUrl) return ''
      if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
        return fileUrl
      }
      const baseUrl = process.env.VUE_APP_BASE_URL || ''
      return baseUrl + fileUrl
    },

    async openRequirement(course) {
      const { courseCode, courseName } = course
      this.requirementTitle = `${courseCode}-${courseName} 考核要求`
      this.currentRequirement = { requirementContent: '', attachments: [] }
      this.requirementVisible = true
      this.requirementLoading = true

      try {
        const res = await getStudentAssessmentRequirement(course.id)
        if (res) {
          this.currentRequirement = res
        }
      } catch {
        // 查询失败，保留默认空数据
      } finally {
        this.requirementLoading = false
      }
    },

    handleRequirementClose() {
      this.currentRequirement = {
        requirementContent: '',
        attachments: []
      }
      this.requirementTitle = ''
      this.requirementLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.practice-body {
  padding: 0 30px 30px;
  min-height: 400px;
}

.batch-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  margin-bottom: 20px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
}

.batch-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fafafa;
  border-bottom: 1px solid #ebeef5;
}

.batch-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.batch-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.batch-time {
  font-size: 13px;
  color: #909399;
  white-space: nowrap;
}

.status-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 3px;
  font-size: 12px;
  line-height: 20px;

  &--sm {
    padding: 1px 8px;
    font-size: 12px;
  }

  &--warning {
    color: #e6a23c;
    background: #fdf6ec;
    border: 1px solid #f5dab1;
  }

  &--success {
    color: #67c23a;
    background: #f0f9eb;
    border: 1px solid #c2e7b0;
  }

  &--danger {
    color: #f56c6c;
    background: #fef0f0;
    border: 1px solid #fbc4c4;
  }

  &--info {
    color: #409eff;
    background: #ecf5ff;
    border: 1px solid #b3d8ff;
  }
}

.course-list {
  padding: 0 20px;
}

.course-item {
  padding: 18px 0;
  border-bottom: 1px dashed #e4e7ed;

  &:last-child {
    border-bottom: none;
  }
}

.course-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.course-info {
  flex: 1;
  min-width: 0;
}

.course-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.course-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.file-link {
  margin-bottom: 8px;

  a {
    color: $primary;
    font-size: 13px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.review-box {
  margin-top: 10px;
  padding: 12px 14px;
  background: #f5f7fa;
  border-radius: 4px;
  border-left: 3px solid #dcdfe6;
}

.review-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 6px;
}

.review-content {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}

.course-action {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.score {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;

  &--pass {
    color: #67c23a;
  }

  &--fail {
    color: #f56c6c;
  }
}

.score-pending {
  font-size: 16px;
  font-weight: 600;
  color: #e6a23c;
  line-height: 1;
}

.teacher-info {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
  line-height: 1;
}

.teacher-label {
  color: #909399;
}

.teacher-name {
  color: #909399;
}

.teacher-phone {
  color: #909399;
}

.file-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.requirement-btn {
  flex-shrink: 0;
}

.practice-video-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  font-size: 14px;
  cursor: pointer;
}

.practice-video-name {
  color: #0075c9;
  cursor: pointer;

  i {
    margin-right: 4px;
  }
}

.practice-video-size {
  color: #999;
  font-size: 12px;
}

.upload-area {
  ::v-deep .el-upload-dragger {
    width: 100%;
    height: auto;
    min-height: 200px;
    padding: 32px 24px;
    border-color: #dcdfe6;
    border-radius: 8px;
    transition: border-color 0.2s, background-color 0.2s;

    &:hover {
      border-color: $primary;
      background: $primary-light-3;
    }
  }

  ::v-deep .el-upload__text {
    margin-top: 12px;
    color: #606266;

    em {
      color: $primary;
      font-style: normal;
      font-weight: 500;
    }
  }

  ::v-deep .el-upload-list__item {
    margin-top: 12px;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 8px 10px;
    background: #fafafa;
  }
}
</style>

<style lang="scss">
@import '@/variable.scss';

.upload-report-dialog {
  .el-dialog__body {
    padding: 24px 28px 12px !important;
  }

  .el-dialog__footer {
    padding: 0 28px 20px !important;
  }

  .upload-dialog-body {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .upload-course-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    background: $primary-light-2;
    border: 1px solid $primary-border;
    border-radius: 8px;
  }

  .upload-course-card__icon {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: #fff;
    color: $primary;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    box-shadow: 0 2px 8px $primary-shadow;
  }

  .upload-course-card__info {
    flex: 1;
    min-width: 0;
  }

  .upload-course-card__label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 4px;
  }

  .upload-course-card__name {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    line-height: 1.4;
  }

  .upload-course-card__batch {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
  }

  .upload-mode-tag {
    flex-shrink: 0;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    line-height: 1.4;

    &.is-new {
      color: $primary;
      background: #fff;
      border: 1px solid $primary-border;
    }

    &.is-modify {
      color: #e6a23c;
      background: #fdf6ec;
      border: 1px solid #f5dab1;
    }
  }

  .upload-current-file {
    padding: 16px 18px;
    background: #f5f7fa;
    border-radius: 8px;
    border: 1px solid #ebeef5;
  }

  .upload-current-file__label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 8px;
  }

  .upload-current-file__content {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;

    i {
      color: $primary;
      font-size: 16px;
    }

    a {
      color: $primary;
      text-decoration: none;
      word-break: break-all;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .video-current-name {
    flex: 1;
    min-width: 0;
    color: #303133;
    word-break: break-all;
  }

  .video-current-size {
    flex-shrink: 0;
    font-size: 12px;
    color: #909399;
  }

  .video-delete-btn {
    color: #f56c6c;
  }

  .upload-review-tip {
    padding: 16px 18px;
    background: #fef0f0;
    border: 1px solid #fbc4c4;
    border-radius: 8px;
  }

  .upload-review-tip__title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #f56c6c;
    margin-bottom: 8px;
  }

  .upload-review-tip__content {
    font-size: 13px;
    color: #606266;
    line-height: 1.6;
  }

  .upload-section__title {
    margin-bottom: 14px;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
  }

  .upload-section__row {
    display: flex;
    align-items: stretch;
    gap: 16px;
  }

  .upload-section__row .upload-area {
    flex: 1;
    min-width: 0;
  }

  .upload-section__tips {
    flex-shrink: 0;
    width: 260px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 16px;
    font-size: 12px;
    color: #909399;
    line-height: 1.7;
    background: #f5f7fa;
    border: 1px solid #ebeef5;
    border-radius: 8px;

    p {
      margin: 0;
      color: #606266;
      padding-left: 12px;
      position: relative;

      &::before {
        content: '•';
        position: absolute;
        left: 0;
        top: 0;
        color: #909399;
      }

      & + p {
        margin-top: 6px;
      }
    }
  }

  .upload-dragger-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .upload-dragger-content__icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: $primary-light;
    color: $primary;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
  }

  .upload-type-tags {
    display: flex;
    gap: 8px;
    margin-top: 14px;
  }

  .type-tag {
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 20px;

    &--word {
      color: #2b579a;
      background: #eef3fb;
    }

    &--ppt {
      color: #d24726;
      background: #fdf0ed;
    }

    &--pdf {
      color: #e74c3c;
      background: #fdecea;
    }

    &--video {
      color: #1e88e5;
      background: #e8f0fe;
    }
  }
}

.requirement-dialog {
  .el-dialog__body {
    padding: 20px 28px !important;
  }

  .el-dialog__footer {
    padding: 0 28px 20px !important;
  }
}

.video-preview-dialog {
  .el-dialog__body {
    padding: 20px !important;
  }
}

.requirement-body {
  min-height: 80px;
}

.requirement-content {
  font-size: 14px;
  color: #303133;
  line-height: 1.8;
}

.requirement-empty {
  font-size: 14px;
  color: #909399;
  text-align: center;
  padding: 40px 0;
}

.requirement-attachments {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.attachment-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;

  i {
    color: $primary;
    font-size: 16px;
    flex-shrink: 0;
  }
}

.attachment-link {
  color: $primary;
  font-size: 13px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
